<template>
    <div class="customer__form modal modal--default">
        <div class="modal__panel modal__panel--leads-transfer">
            <div class="modal__logo-wrapper">
                <div class="modal__logo"></div>
            </div>

            <div class="modal__main-icon modal__main-icon--leads"></div>
            <div class="modal__close" @click="closeModal"></div>

            <div class="form">
                <div class="navigators">
                    <div class="navigator navigator--active">
                        <div class="navigator__bullet"></div>
                        <div class="navigator__title">
                            {{ $t(`leads.${"transferLeads"}`) }}
                        </div>
                    </div>
                </div>

                <div class="add__form">
                    <transition name="fade" mode="out-in">
                        <Loading v-if="loading" />

                        <div v-else>
                            <div class="form__row">
                                <div class="form__field">
                                    <VSelect
                                        v-model="teamId"
                                        class="select select--default"
                                        :class="{ 'select--error': teamHasError }"
                                        :options="userTeams"
                                        label="name"
                                        :reduce="team => team.id"
                                        :placeholder="$t('settingsPage.selectTeamPlaceholder')"
                                        @update:modelValue="handleTeamChange"
                                    />

                                    <div
                                        v-if="teamHasError"
                                        class="form__error"
                                    >
                                        {{ $t("addTeamModal.teamIdErrorMsg") }}
                                    </div>
                                </div>
                            </div>

                            <div
                                v-if="teamId"
                                class="form__row"
                            >
                                <div class="form__field">
                                    <VSelect
                                        v-model="agentId"
                                        class="select select--default"
                                        :class="{ 'select--error': agentHasError }"
                                        :options="agents"
                                        :reduce="agent => agent.id"
                                        :get-option-label="agent => `${agent.first_name} ${agent.last_name}`"
                                        :placeholder="$t('policyProvision.agent')"
                                    />

                                    <div
                                        v-if="agentHasError"
                                        class="form__error"
                                    >
                                        {{ $t("addTransaction.agentErrorMsg") }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>

            <div
                v-if="isRequesting"
                class="modal__button modal__button--submit button button--default button--loading"
            ></div>

            <button
                v-else
                type="button"
                class="modal__button modal__button--submit button button--default"
                @click="submitTransfer"
            >
                <span>{{ $t(`leads.${"transfer"}`) }}</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import Loading from "@/partials/Loading";

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const teamId = ref(null);
const agentId = ref(null);

const loading = ref(false);
const teamHasError = ref(false);
const agentHasError = ref(false);
const isRequesting = ref(false);

const searchLeadForms = computed(() => store.getters.searchLeadForms);
const leadsToTransfer = computed(() => store.getters.leadsToTransfer);
const sourceTeamId = computed(() => {
    const value = Number(route.query.team_id);
    return Number.isInteger(value) && value > 0 ? value : null;
});

const userTeams = computed(() => {
    if (store.getters.isSuperAdmin) {
        return store.getters.teams || [];
    }

    const authorizedIds = store.getters.authorizedTeamIds("customer_create");
    if (authorizedIds === null) {
        return store.getters.teams || [];
    }
    return store.getters.authorizationMemberships
        .filter((membership) => authorizedIds.includes(membership.team.id))
        .map((membership) => membership.team);
});

const agents = computed(() => {
    return searchLeadForms.value?.agents || [];
});

// const isFormValid = computed(() => {
//     return (
//         teamId.value &&
//         agentId.value &&
//         leadsToTransfer.value.length > 0
//     );
// });

watch(teamId, (value) => {
    if (value) {
        teamHasError.value = false;
    }
});

watch(agentId, (value) => {
    if (value) {
        agentHasError.value = false;
    }
});

onMounted(async () => {
    if (!store.getters.isSuperAdmin && (sourceTeamId.value === null
        || !store.getters.hasTeamPermission("customer_create", sourceTeamId.value))) {
        closeModal();
        return;
    }

    if (store.getters.isSuperAdmin && !store.getters.teams.length) {
        loading.value = true;
        try {
            await store.dispatch("getTeams");
        } finally {
            loading.value = false;
        }
    }
});

async function handleTeamChange(value) {
    teamId.value = value;
    agentId.value = null;

    if (!value) return;

    loading.value = true;

    try {
        await store.dispatch("getSearchLeadForms", value);
    } catch (error) {
        console.error("Error loading team data:", error);
    } finally {
        loading.value = false;
    }
}

function validateForm() {
    let valid = true;

    if (!teamId.value) {
        teamHasError.value = true;
        valid = false;
    } else if (!store.getters.isSuperAdmin && (
        sourceTeamId.value === null
        || !store.getters.hasTeamPermission("customer_create", sourceTeamId.value)
        || !store.getters.hasTeamPermission("customer_create", teamId.value)
    )) {
        store.commit("setToast", {
            display: true,
            type: "error",
            title: t("transferLeadsModal.error") || "Error",
            message: t("noData.noPermissionMessage"),
        });
        valid = false;
    }

    if (!agentId.value) {
        agentHasError.value = true;
        valid = false;
    }

    if (leadsToTransfer.value.length === 0) {
        store.commit("setToast", {
            display: true,
            type: "error",
            title: t("transferLeadsModal.error") || "Error",
            message:
                t("transferLeadsModal.noLeadsSelected") ||
                "No leads selected for transfer",
        });

        valid = false;
    }

    return valid;
}

async function submitTransfer() {
    if (!validateForm()) return;

    isRequesting.value = true;

    try {
        await store.dispatch("transferLeadCustomers", {
            customerIds: leadsToTransfer.value,
            teamId: teamId.value,
            agentId: agentId.value,
        });

        closeModal();
    } catch (error) {
        // Error already handled by store action
    } finally {
        isRequesting.value = false;
    }
}

function closeModal() {
    teamId.value = null;
    agentId.value = null;

    teamHasError.value = false;
    agentHasError.value = false;

    store.commit("setLeadsToTransfer", []);
    store.commit("setShowTransferLeadsModal", false);
}
</script>

<style scoped lang="scss">
.modal__button--submit {
    width: 150px;
}

.modal__panel--leads-transfer {
    width: 600px !important;
}

.transfer-info {
    padding: 15px 20px;
    margin: 15px 0;
    background: var(--bg-3);
    border-radius: var(--radius-1);
    border-left: 4px solid var(--primary);

    p {
        margin: 0;
        color: var(--text-1);
        font-size: 14px;

        strong {
            color: var(--primary);
            font-size: 16px;
        }
    }
}

.form__label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--text-1);

    .required {
        color: var(--error);
        margin-left: 2px;
    }
}

.textarea {
    width: 100%;
    resize: vertical;
    min-height: 80px;
    padding: 12px;
    font-family: inherit;
    line-height: 1.5;

    &::placeholder {
        color: var(--text-7);
    }
}

.form__field {
    width: 100% !important;
}

.form__row {
    margin: 20px 0;
}

.button--default:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
        background: var(--primary);
    }
} 
</style>
